import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../utils';

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipBase = TooltipPrimitive.Root;

TooltipBase.displayName = 'TooltipBase';

const TooltipTrigger = TooltipPrimitive.Trigger;

interface TooltipArrowProps
  extends React.ComponentProps<typeof TooltipPrimitive.Arrow> {
  className?: string;
}

const TooltipArrow = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Arrow>,
  TooltipArrowProps
>(({ className, width = 8, height = 4, ...props }, ref) => (
  <TooltipPrimitive.Arrow
    ref={ref}
    className={cn('fill-primary', className)}
    width={width}
    height={height}
    {...props}
  />
));

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    withArrow?: boolean;
    TooltipArrowProps?: TooltipArrowProps;
  }
>(
  (
    { className, sideOffset = 8, withArrow, TooltipArrowProps, ...props },
    ref
  ) => (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'bg-primary text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-hidden rounded-md px-3 py-1.5 text-xs',
        className
      )}
      {...props}
    >
      {props.children}
      {withArrow && <TooltipArrow {...TooltipArrowProps} />}
    </TooltipPrimitive.Content>
  )
);

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface RichTooltipProps extends TooltipPrimitive.TooltipProps {
  content?: React.ReactNode;
  TooltipContentProps?: React.ComponentProps<typeof TooltipContent>;
  TooltipTriggerProps?: React.ComponentProps<typeof TooltipTrigger>;
}

const Tooltip: React.FC<RichTooltipProps> = ({
  children,
  content,
  TooltipContentProps,
  TooltipTriggerProps,
  ...props
}) => (
  <TooltipBase delayDuration={150} {...props}>
    <TooltipTrigger {...TooltipTriggerProps}>{children}</TooltipTrigger>
    {content && (
      <TooltipPrimitive.Portal>
        <TooltipContent {...TooltipContentProps}>{content}</TooltipContent>
      </TooltipPrimitive.Portal>
    )}
  </TooltipBase>
);

Tooltip.displayName = 'Tooltip';

export {
  Tooltip,
  TooltipBase,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
};
